import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HomeIcon from '@mui/icons-material/Home';
import PageShell from '../components/PageShell';
import TermsDialog from '../components/TermsDialog';
import request from '../api';

const RESEND_COOLDOWN_MS = 60 * 1000;

function VerifyStoreEmailStep({ applicationId, email, onVerified }) {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);
    const [resendUntil, setResendUntil] = useState(0);
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        if (!resendUntil) return undefined;
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, [resendUntil]);

    const cooldownRemaining = Math.max(0, resendUntil - now);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setNotice('');
        setBusy(true);
        try {
            await request(`/store-applications/${applicationId}/verify-email`, {
                method: 'POST',
                body: { code },
            });
            onVerified();
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const resend = async () => {
        setError('');
        setNotice('');
        try {
            await request(`/store-applications/${applicationId}/resend-verification`, { method: 'POST' });
            setNotice('Código reenviado. Revisa tu correo.');
            setResendUntil(Date.now() + RESEND_COOLDOWN_MS);
            setNow(Date.now());
        } catch (err) {
            if (err.code === 'RATE_LIMITED' && err.retryAfterMs) {
                setResendUntil(Date.now() + err.retryAfterMs);
                setNow(Date.now());
            }
            setError(err.message);
        }
    };

    return (
        <Paper sx={{ maxWidth: 420, mx: 'auto', p: { xs: 2.5, sm: 3.5 } }}>
            <Typography variant="h6" gutterBottom>Verifica tu Correo de Contacto</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enviamos un código de verificación de 6 dígitos a {email}. Ingrésalo para continuar.
            </Typography>
            <Box component="form" onSubmit={submit}>
                <Stack spacing={2}>
                    <TextField
                        label="Código de Verificación"
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        fullWidth
                        required
                        autoFocus
                        inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                    />
                    <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
                        Verificar
                    </Button>
                    {error && <Alert severity="error">{error}</Alert>}
                    {notice && <Alert severity="success">{notice}</Alert>}
                    <Button onClick={resend} color="inherit" disabled={cooldownRemaining > 0}>
                        {cooldownRemaining > 0
                            ? `Reenviar código (${Math.ceil(cooldownRemaining / 1000)}s)`
                            : 'Reenviar código'}
                    </Button>
                </Stack>
            </Box>
        </Paper>
    );
}

function StoreRegister() {
    const [storeName, setStoreName] = useState('');
    const [contactName, setContactName] = useState('');
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [addressStreet, setAddressStreet] = useState('');
    const [addressNumber, setAddressNumber] = useState('');
    const [addressDelegacion, setAddressDelegacion] = useState('');
    const [deliveryElsewhere, setDeliveryElsewhere] = useState(false);
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [application, setApplication] = useState(null);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!termsAccepted) {
            setError('Debes aceptar los Términos y Condiciones para registrar tu tienda');
            return;
        }
        if (deliveryElsewhere && !deliveryAddress.trim()) {
            setError('Ingresa la dirección de entrega');
            return;
        }
        setError('');
        setBusy(true);
        try {
            const data = await request('/store-applications', {
                method: 'POST',
                body: {
                    store_name: storeName,
                    contact_name: contactName,
                    email,
                    whatsapp,
                    address_street: addressStreet,
                    address_number: addressNumber,
                    address_delegacion: addressDelegacion,
                    delivery_elsewhere: deliveryElsewhere,
                    delivery_address: deliveryElsewhere ? deliveryAddress : null,
                    terms_accepted: termsAccepted,
                },
            });
            setApplication(data.application);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const nav = (
        <>
            <Button component={RouterLink} to="/store/login" color="inherit">Iniciar Sesión</Button>
            <Button component={RouterLink} to="/" color="inherit" startIcon={<HomeIcon />}>Volver al Inicio</Button>
        </>
    );

    if (done) {
        return (
            <PageShell title="Registro de Tienda" nav={nav} maxWidth="sm">
                <Paper sx={{ maxWidth: 420, mx: 'auto', p: { xs: 2.5, sm: 3.5 }, textAlign: 'center' }}>
                    <StorefrontIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6" gutterBottom>¡Correo verificado!</Typography>
                    <Typography color="text.secondary">
                        Tu solicitud fue enviada correctamente. Un administrador la revisará y te
                        contactará con tus datos de acceso una vez aprobada.
                    </Typography>
                </Paper>
            </PageShell>
        );
    }

    if (application) {
        return (
            <PageShell title="Registro de Tienda" nav={nav} maxWidth="sm">
                <VerifyStoreEmailStep
                    applicationId={application.id}
                    email={email}
                    onVerified={() => setDone(true)}
                />
            </PageShell>
        );
    }

    return (
        <PageShell title="Registro de Tienda" nav={nav} maxWidth="sm">
            <Paper sx={{ maxWidth: 480, mx: 'auto', p: { xs: 2.5, sm: 3.5 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <StorefrontIcon color="primary" />
                    <Typography variant="h6">Registra tu Tienda</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Completa tus datos para solicitar una cuenta de tienda. Un administrador revisará
                    tu solicitud y te asignará tus credenciales de acceso.
                </Typography>
                <Box component="form" onSubmit={submit}>
                    <Stack spacing={2}>
                        <TextField
                            label="Nombre de la Tienda"
                            value={storeName}
                            onChange={(e) => setStoreName(e.target.value)}
                            fullWidth
                            required
                            autoFocus
                        />
                        <TextField
                            label="Nombre del Contacto"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Correo de Contacto"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="WhatsApp de Contacto"
                            value={whatsapp}
                            onChange={(e) => setWhatsapp(e.target.value)}
                            fullWidth
                            required
                        />

                        <Divider />
                        <Typography variant="subtitle2">Dirección de la Tienda</Typography>
                        <TextField
                            label="Calle"
                            value={addressStreet}
                            onChange={(e) => setAddressStreet(e.target.value)}
                            fullWidth
                            required
                        />
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Número"
                                value={addressNumber}
                                onChange={(e) => setAddressNumber(e.target.value)}
                                fullWidth
                                required
                            />
                            <TextField
                                label="Delegación"
                                value={addressDelegacion}
                                onChange={(e) => setAddressDelegacion(e.target.value)}
                                fullWidth
                                required
                            />
                        </Stack>

                        {/* Oculto por el momento — se reactivará más adelante.
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={deliveryElsewhere}
                                    onChange={(e) => setDeliveryElsewhere(e.target.checked)}
                                />
                            }
                            label="Las entregas se realizan en otro lado"
                        />
                        {deliveryElsewhere && (
                            <TextField
                                label="Dirección de Entrega"
                                value={deliveryAddress}
                                onChange={(e) => setDeliveryAddress(e.target.value)}
                                fullWidth
                                required
                                multiline
                                minRows={2}
                            />
                        )}
                        */}

                        <Divider />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={termsAccepted}
                                    onChange={(e) => setTermsAccepted(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    He leído y acepto los{' '}
                                    <Link
                                        component="button"
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setTermsOpen(true);
                                        }}
                                    >
                                        Términos y Condiciones
                                    </Link>
                                </Typography>
                            }
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={busy || !termsAccepted}
                            fullWidth
                        >
                            Enviar Solicitud
                        </Button>
                        {error && <Alert severity="error">{error}</Alert>}
                    </Stack>
                </Box>
                <TermsDialog open={termsOpen} onClose={() => setTermsOpen(false)} />
            </Paper>
        </PageShell>
    );
}

export default StoreRegister;
